// services/resultEntryService.js
import axios from 'axios';
import { BASE_URL } from '../utils/BaseUrl';

// Create axios instance with interceptors
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {

    const user = JSON.parse(localStorage.getItem("user"));

    const token = user?.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const resultEntryService = {
  // ==================== EXISTING METHODS ====================

  // Get pending tests for result entry
  getPendingTests: async (page = 1, limit = 20, search = '', status = '') => {
    const response = await api.get('/results/bystatus', {
      params: { page, limit, search, status }
    });
    return response.data;
  },

  // Get result entry by ID
  getResultById: async (resultId) => {
    const response = await api.get(`/results/${resultId}`);
    return response.data;
  },

  // Initialize result entry for a booking
  initializeResultEntry: async (bookingId) => {
    const response = await api.get(`/results/initialize/${bookingId}`);
    return response.data;
  },

  // Update test results for a service
  updateTestResults: async (resultId, serviceId, updateData) => {
    const response = await api.put(
      `/results/update/${resultId}/service/${serviceId}`,
      updateData
    );
    return response.data;
  },

  // Frontend calculation
  calculateOnFrontend: async (formula, values) => {
    const response = await api.post('/result-entry/calculate-frontend', {
      formula,
      values
    });
    return response.data;
  },

  // Verify results
  verifyResults: async (resultId, serviceId, verificationData) => {
    const response = await api.post(
      `/results/verify/${resultId}/service/${serviceId}`,
      verificationData
    );
    return response.data;
  },

  // Release results
  releaseResults: async (resultId, releaseData) => {
    const response = await api.post(
      `/results/release/${resultId}`,
      releaseData
    );
    return response.data;
  },

  // Get result by barcode
  getResultByBarcode: async (barcode) => {
    const response = await api.get(`/results/barcode/${barcode}`);
    return response.data;
  },

  // Calculate derived results
  calculateDerivedResults: async (resultId, serviceId) => {
    const response = await api.post(
      `/results/calculate/${resultId}/service/${serviceId}`
    );
    return response.data;
  },

  // ==================== DATE EDITING METHODS ====================

  // Update result dates (collection, received, reported)
  updateResultDates: async (resultId, dateData) => {
    const response = await api.put(`/results/${resultId}/dates`, dateData);
    return response.data;
  },

  // Update service-specific dates (testedAt, completedAt)
  updateServiceDates: async (resultId, serviceId, dateData) => {
    const response = await api.put(
      `/results/${resultId}/services/${serviceId}/dates`,
      dateData
    );
    return response.data;
  },

  // Get date summary
  getDateSummary: async (resultId) => {
    const response = await api.get(`/results/${resultId}/date-summary`);
    return response.data;
  },

  // Get date edit history
  getDateEditHistory: async (resultId) => {
    const response = await api.get(`/results/${resultId}/date-history`);
    return response.data;
  },

  // Get tests by date range
  getTestsByDateRange: async (filters = {}) => {
    const response = await api.get('/results/date-range/filter', {
      params: filters
    });
    return response.data;
  },

  // ==================== DELETE/CANCEL METHODS ====================

  // Cancel entire test report
  cancelTestReport: async (resultId, cancellationData) => {
    const response = await api.put(`/results/${resultId}/cancel`, cancellationData);
    return response.data;
  },

  // Restore cancelled test report
  restoreTestReport: async (resultId, restoreData) => {
    const response = await api.put(`/results/${resultId}/restore`, restoreData);
    return response.data;
  },

  // Soft delete test report
  softDeleteTestReport: async (resultId, deleteData) => {
    const response = await api.put(`/results/${resultId}/soft-delete`, deleteData);
    return response.data;
  },

  // Restore deleted test report
  restoreDeletedTestReport: async (resultId, restoreData) => {
    const response = await api.put(`/results/${resultId}/restore-deleted`, restoreData);
    return response.data;
  },

  // Permanent delete test report (admin only)
  permanentDeleteTestReport: async (resultId) => {
    const response = await api.delete(`/results/${resultId}/permanent-delete`);
    return response.data;
  },

  // ==================== SERVICE-LEVEL OPERATIONS ====================

  // Cancel individual service
  cancelService: async (resultId, serviceId, cancellationData) => {
    const response = await api.put(
      `/results/${resultId}/services/${serviceId}/cancel`,
      cancellationData
    );
    return response.data;
  },

  // Restore individual service
  restoreService: async (resultId, serviceId, restoreData) => {
    const response = await api.put(
      `/results/${resultId}/services/${serviceId}/restore`,
      restoreData
    );
    return response.data;
  },

  // ==================== HISTORY & REPORTS ====================

  // Get cancellation/deletion history
  getCancellationHistory: async (resultId) => {
    const response = await api.get(`/results/${resultId}/cancellation-history`);
    return response.data;
  },

  // Get deleted/cancelled reports
  getDeletedOrCancelledReports: async (params = {}) => {
    const response = await api.get('/results/reports/deleted-cancelled', {
      params
    });
    return response.data;
  },

  // ==================== BULK OPERATIONS (Optional) ====================

  // Bulk cancel tests
  bulkCancelTests: async (cancellationData) => {
    const response = await api.post('/results/bulk/cancel', cancellationData);
    return response.data;
  },

  // Bulk delete tests
  bulkDeleteTests: async (deleteData) => {
    const response = await api.post('/results/bulk/delete', deleteData);
    return response.data;
  },

  // Bulk restore tests
  bulkRestoreTests: async (restoreData) => {
    const response = await api.post('/results/bulk/restore', restoreData);
    return response.data;
  },

  // ==================== DASHBOARD & STATISTICS ====================

  // Get dashboard statistics
  getDashboardStats: async (params = {}) => {
    const response = await api.get('/results/stats/dashboard', {
      params
    });
    return response.data;
  },

  // Get cancellation statistics
  getCancellationStats: async (params = {}) => {
    const response = await api.get('/results/stats/cancellations', {
      params
    });
    return response.data;
  },

  // ==================== HELPER METHODS ====================

  // Check if user can edit dates (frontend validation)
  canEditDates: (resultData) => {
    if (!resultData) return false;
    return !(
      resultData.overall_status === 'released' ||
      resultData.cancelled ||
      resultData.deleted
    );
  },

  // Check if test can be cancelled/deleted (frontend validation)
  canBeCancelledOrDeleted: (resultData) => {
    if (!resultData) return false;
    return !(
      resultData.overall_status === 'released' ||
      resultData.cancelled ||
      resultData.deleted
    );
  },

  // Format date for display
  formatDate: (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  },

  // Get status config for UI display
  getStatusConfig: (status) => {
    const configs = {
      pending: { color: 'warning', label: 'Pending', icon: '⏳' },
      testing: { color: 'info', label: 'Testing', icon: '🔬' },
      completed: { color: 'success', label: 'Completed', icon: '✅' },
      verified: { color: 'primary', label: 'Verified', icon: '🔒' },
      released: { color: 'secondary', label: 'Released', icon: '📄' },
      cancelled: { color: 'error', label: 'Cancelled', icon: '❌' },
      deleted: { color: 'default', label: 'Deleted', icon: '🗑️' }
    };
    return configs[status] || configs.pending;
  },



  getAllTechnicians: async (search = '') => {
    const response = await api.get('/technicians', {
      params: { search }
    });
    return response.data;
  },

  // Get technician by ID
  getTechnicianById: async (technicianId) => {
    const response = await api.get(`/technicians/${technicianId}`);
    return response.data;
  },

  // Get technicians by section
  getTechniciansBySection: async (section) => {
    const response = await api.get(`/technicians/section/${section}`);
    return response.data;
  }
};

export default api;